import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './LookbookPage.css';

const emptyJobForm = {
  shopifyProductId: '',
  productImageUrl: '',
  fabricImageUrl: '',
  sampleCount: 1,
  aspectRatio: '3:4',
  notes: '',
  forceRegenerate: false,
  storeFinalOnly: true,
  generationFlow: 'gemini_flash_image_edit',
};

const emptyCollectionForm = {
  collectionGid: '',
  aspectRatio: 'random',
  forceRegenerate: false,
  storeFinalOnly: true,
  generationFlow: 'gemini_flash_image_edit',
};

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function resolveMediaUrl(rawUrl) {
  const url = String(rawUrl || '').trim();
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (!url.startsWith('/')) return url;
  const apiBase = String(process.env.REACT_APP_API_BASE_URL || '').trim();
  return apiBase ? `${apiBase}${url}` : url;
}

function extractIntermediateModelImages(source = {}) {
  const direct = Array.isArray(source?.intermediateModelImages) ? source.intermediateModelImages : [];
  const nested = Array.isArray(source?.generationParams?.intermediateModelImages)
    ? source.generationParams.intermediateModelImages
    : [];
  const list = direct.length ? direct : nested;
  return list
    .filter((item) => item && item.url)
    .map((item, index) => ({
      ...item,
      key: `${String(item.filePath || item.url || index)}-${index}`,
      resolvedUrl: resolveMediaUrl(item.url),
    }));
}

const PUBLISH_STEP_ORDER = ['fileCreated', 'readyVerified', 'metafieldsSet', 'metaobjectCreated', 'collectionUpdated'];

function LookbookPage() {
  const { currentUser } = useAuth();
  const [entries, setEntries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [jobForm, setJobForm] = useState(emptyJobForm);
  const [jobProductSearch, setJobProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsTotalPages, setJobsTotalPages] = useState(1);
  const [jobsTotalCount, setJobsTotalCount] = useState(0);
  const [busyJobCreate, setBusyJobCreate] = useState(false);
  const [busyRunJob, setBusyRunJob] = useState(false);
  const [processingEntryId, setProcessingEntryId] = useState('');
  const [processingEntryAction, setProcessingEntryAction] = useState('');
  const [collections, setCollections] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [collectionForm, setCollectionForm] = useState(emptyCollectionForm);
  const [busyCollectionQueue, setBusyCollectionQueue] = useState(false);
  const [statusFilter, setStatusFilter] = useState('generated');
  const [createdSortDir, setCreatedSortDir] = useState('desc');
  const [entriesProductSearch, setEntriesProductSearch] = useState('');
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesTotalPages, setEntriesTotalPages] = useState(1);
  const [entriesTotalCount, setEntriesTotalCount] = useState(0);
  const [statesOpen, setStatesOpen] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [entriesCollectionGid, setEntriesCollectionGid] = useState('');
  const [publishAfterInput, setPublishAfterInput] = useState('');
  const [promptSettingsOpen, setPromptSettingsOpen] = useState(false);
  const [promptSettingsLoading, setPromptSettingsLoading] = useState(false);
  const [promptSettingsSaving, setPromptSettingsSaving] = useState(false);
  const [promptSettingsSearch, setPromptSettingsSearch] = useState('');
  const [promptSpecs, setPromptSpecs] = useState([]);
  const [selectedPromptSpecId, setSelectedPromptSpecId] = useState('');
  const [newPromptText, setNewPromptText] = useState('');
  const pendingQueueActionRef = useRef(null);
  const [dailyMetrics, setDailyMetrics] = useState({ rows: [], totals: { generated: 0, estimatedCost: 0 }, unitCost: 0, timeZone: 'Asia/Kolkata', days: 30 });
  const [dailyMetricsLoading, setDailyMetricsLoading] = useState(false);
  const [dailyMetricsDays, setDailyMetricsDays] = useState(30);
  const [dailyUnitCost, setDailyUnitCost] = useState('0');
  const [extraPromptRules, setExtraPromptRules] = useState([]);
  const [extraPromptRulesOpen, setExtraPromptRulesOpen] = useState(false);
  const [extraPromptRulesLoading, setExtraPromptRulesLoading] = useState(false);
  const [extraPromptRulesDraft, setExtraPromptRulesDraft] = useState('');
  const [extraPromptRulesSaving, setExtraPromptRulesSaving] = useState(false);

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadJobs = useCallback(async (page = 1) => {
    setJobsLoading(true);
    try {
      const resp = await authedFetch(`/api/v1/lookbook/generation-jobs?limit=10&page=${Math.max(1, Number(page) || 1)}&visible=pending_failed`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load lookbook generation jobs.');
      }
      setJobs(Array.isArray(data?.jobs) ? data.jobs : []);
      const pagination = data?.pagination || {};
      setJobsPage(Math.max(1, Number(pagination.page || page) || 1));
      setJobsTotalPages(Math.max(1, Number(pagination.totalPages || 1) || 1));
      setJobsTotalCount(Math.max(0, Number(pagination.totalCount || 0) || 0));
    } catch (err) {
      setError(err.message || 'Failed to load lookbook generation jobs.');
    } finally {
      setJobsLoading(false);
    }
  }, [authedFetch]);

  const handleShowExtraPromptRules = useCallback(async () => {
    if (extraPromptRulesOpen) {
      setExtraPromptRulesOpen(false);
      return;
    }
    if (Array.isArray(extraPromptRules) && extraPromptRules.length > 0) {
      setExtraPromptRulesOpen(true);
      return;
    }
    setExtraPromptRulesLoading(true);
    setError('');
    try {
      const resp = await authedFetch('/api/v1/lookbook/prompt-rules');
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load prompt rules.');
      }
      const rules = Array.isArray(data?.rules) ? data.rules.map((item) => String(item || '').trim()).filter(Boolean) : [];
      setExtraPromptRules(rules);
      setExtraPromptRulesDraft(rules.join('\n'));
      setExtraPromptRulesOpen(true);
    } catch (err) {
      setError(err.message || 'Failed to load prompt rules.');
    } finally {
      setExtraPromptRulesLoading(false);
    }
  }, [authedFetch, extraPromptRules, extraPromptRulesOpen]);

  const handleSaveExtraPromptRules = useCallback(async () => {
    setError('');
    setMessage('');
    setExtraPromptRulesSaving(true);
    try {
      const resp = await authedFetch('/api/v1/lookbook/prompt-rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rulesText: extraPromptRulesDraft }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to update prompt rules.');
      }
      const rules = Array.isArray(data?.rules) ? data.rules.map((item) => String(item || '').trim()).filter(Boolean) : [];
      setExtraPromptRules(rules);
      setExtraPromptRulesDraft(rules.join('\n'));
      setMessage(data?.message || 'Prompt rules updated.');
    } catch (err) {
      setError(err.message || 'Failed to update prompt rules.');
    } finally {
      setExtraPromptRulesSaving(false);
    }
  }, [authedFetch, extraPromptRulesDraft]);

  const loadStates = useCallback(async () => {
    setStatesLoading(true);
    try {
      const statesResp = await authedFetch('/api/v1/lookbook/product-states?limit=100');
      const statesData = await statesResp.json();
      if (!statesResp.ok || statesData?.success === false) {
        throw new Error(statesData?.message || 'Failed to load lookbook product states.');
      }
      setStates(Array.isArray(statesData?.states) ? statesData.states : []);
    } catch (err) {
      setError(err.message || 'Failed to load lookbook product states.');
    } finally {
      setStatesLoading(false);
    }
  }, [authedFetch]);

  const loadPromptSpecs = useCallback(async (query = '') => {
    setPromptSettingsLoading(true);
    try {
      const resp = await authedFetch(`/api/v1/lookbook/fabric-prompts?q=${encodeURIComponent(String(query || '').trim())}`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load fabric prompt settings.');
      }
      const specs = Array.isArray(data?.specifications) ? data.specifications : [];
      setPromptSpecs(specs);
      if (!specs.length) {
        setSelectedPromptSpecId('');
      } else if (!selectedPromptSpecId || !specs.some((spec) => spec.id === selectedPromptSpecId)) {
        setSelectedPromptSpecId(specs[0].id);
      }
      return specs;
    } catch (err) {
      setError(err.message || 'Failed to load fabric prompt settings.');
      return [];
    } finally {
      setPromptSettingsLoading(false);
    }
  }, [authedFetch, selectedPromptSpecId]);

  const openPromptSettings = useCallback(async ({ specId = '', query = '', pendingAction = null } = {}) => {
    if (pendingAction) {
      pendingQueueActionRef.current = pendingAction;
    }
    setPromptSettingsOpen(true);
    const specs = await loadPromptSpecs(query);
    const normalizedSpecId = String(specId || '').trim();
    if (normalizedSpecId) {
      const direct = specs.find((spec) => spec.id === normalizedSpecId);
      if (direct) {
        setSelectedPromptSpecId(direct.id);
      } else {
        const grouped = specs.find((spec) => Array.isArray(spec.specIds) && spec.specIds.includes(normalizedSpecId));
        if (grouped) setSelectedPromptSpecId(grouped.id);
      }
    }
  }, [loadPromptSpecs]);

  const fetchEntries = useCallback(async () => {
    const params = new URLSearchParams();
    params.set('limit', '20');
    params.set('page', String(Math.max(1, Number(entriesPage) || 1)));
    if (statusFilter) params.set('status', statusFilter);
    if (entriesProductSearch.trim()) params.set('shopifyProductId', entriesProductSearch.trim());
    if (entriesCollectionGid) params.set('collectionGid', entriesCollectionGid);
    params.set('sortBy', 'createdAt');
    params.set('sortDir', createdSortDir === 'asc' ? 'asc' : 'desc');
    const entriesResp = await authedFetch(`/api/v1/lookbook/entries?${params.toString()}`);
    const entriesData = await entriesResp.json();
    if (!entriesResp.ok || entriesData?.success === false) {
      throw new Error(entriesData?.message || 'Failed to load lookbook entries.');
    }
    return entriesData;
  }, [authedFetch, entriesPage, statusFilter, createdSortDir, entriesProductSearch, entriesCollectionGid]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const entriesData = await fetchEntries();
      setEntries(Array.isArray(entriesData?.entries) ? entriesData.entries : []);
      const pagination = entriesData?.pagination || {};
      setEntriesPage(Math.max(1, Number(pagination.page || entriesPage) || 1));
      setEntriesTotalPages(Math.max(1, Number(pagination.totalPages || 1) || 1));
      setEntriesTotalCount(Math.max(0, Number(pagination.totalCount || 0) || 0));
    } catch (err) {
      setError(err.message || 'Failed to load lookbook entries.');
    } finally {
      setLoading(false);
    }
  }, [fetchEntries, entriesPage]);

  const refreshEntriesSilent = useCallback(async () => {
    try {
      const entriesData = await fetchEntries();
      setEntries(Array.isArray(entriesData?.entries) ? entriesData.entries : []);
      const pagination = entriesData?.pagination || {};
      setEntriesPage(Math.max(1, Number(pagination.page || entriesPage) || 1));
      setEntriesTotalPages(Math.max(1, Number(pagination.totalPages || 1) || 1));
      setEntriesTotalCount(Math.max(0, Number(pagination.totalCount || 0) || 0));
    } catch (err) {
      console.warn('Failed to refresh lookbook entries:', err?.message || err);
    }
  }, [fetchEntries, entriesPage]);

  const loadDailyMetrics = useCallback(async () => {
    setDailyMetricsLoading(true);
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
      const params = new URLSearchParams();
      params.set('days', String(Math.max(1, Number(dailyMetricsDays) || 30)));
      params.set('timeZone', timeZone);
      params.set('unitCost', String(Math.max(0, Number(dailyUnitCost) || 0)));
      const resp = await authedFetch(`/api/v1/lookbook/metrics/daily?${params.toString()}`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load lookbook daily metrics.');
      }
      setDailyMetrics({
        rows: Array.isArray(data?.rows) ? data.rows : [],
        totals: data?.totals || { generated: 0, estimatedCost: 0 },
        unitCost: Number(data?.unitCost || 0) || 0,
        timeZone: String(data?.timeZone || timeZone),
        days: Number(data?.days || dailyMetricsDays) || dailyMetricsDays,
      });
    } catch (err) {
      setError(err.message || 'Failed to load lookbook daily metrics.');
    } finally {
      setDailyMetricsLoading(false);
    }
  }, [authedFetch, dailyMetricsDays, dailyUnitCost]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadDailyMetrics();
  }, [loadDailyMetrics]);

  useEffect(() => {
    if (!jobsOpen) return;
    loadJobs(jobsPage);
  }, [jobsOpen, jobsPage, loadJobs]);

  useEffect(() => {
    if (!statesOpen) return;
    loadStates();
  }, [statesOpen, loadStates]);

  const searchProducts = useCallback(async (query) => {
    const trimmed = String(query || '').trim();
    setJobProductSearch(trimmed);
    if (!trimmed) {
      setProductResults([]);
      setSelectedProduct(null);
      return;
    }
    setProductSearchLoading(true);
    try {
      const normalizedQuery = trimmed.toUpperCase();
      const resp = await authedFetch(`/api/v1/products?page=1&limit=25&search=${encodeURIComponent(normalizedQuery)}`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to search products.');
      }
      const rawProducts = Array.isArray(data?.products) ? data.products : [];
      const results = rawProducts.map((product) => {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const firstVariant = variants[0] || {};
        const images = Array.isArray(product?.images) ? product.images : [];
        const variantImage = images.find((img) => img?.id === firstVariant?.image_id);
        return {
          id: String(product?.id || '').trim(),
          title: String(product?.title || '').trim(),
          handle: String(product?.handle || '').trim() || null,
          imageUrl: String(variantImage?.src || product?.image?.src || '').trim() || null,
          sku: String(firstVariant?.sku || '').trim() || null,
          variantCount: variants.length,
          raw: product,
        };
      }).filter((product) => {
        const haystack = [
          product.id,
          product.title,
          product.handle,
          product.sku,
        ].filter(Boolean).join(' ').toUpperCase();
        return haystack.includes(normalizedQuery);
      });
      setProductResults(results);
    } catch (err) {
      setError(err.message || 'Failed to search products.');
      setProductResults([]);
    } finally {
      setProductSearchLoading(false);
    }
  }, [authedFetch]);

  const loadCollections = useCallback(async (query = '') => {
    try {
      const resp = await authedFetch(`/api/v1/lookbook/collections?q=${encodeURIComponent(String(query || '').trim())}&limit=2000`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load collections.');
      }
      setCollections(Array.isArray(data?.collections) ? data.collections : []);
    } catch (err) {
      setError(err.message || 'Failed to load collections.');
    }
  }, [authedFetch]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const handleCreateJob = useCallback(async (event, generationFlowOverride = null) => {
    event.preventDefault();
    setBusyJobCreate(true);
    setError('');
    setMessage('');
    try {
      if (!String(jobForm.shopifyProductId || '').trim()) {
        throw new Error('Select a product first.');
      }
      const payload = {
        shopifyProductId: jobForm.shopifyProductId.trim(),
        productImageUrl: jobForm.productImageUrl.trim(),
        fabricImageUrl: jobForm.fabricImageUrl.trim(),
        sampleCount: Number(jobForm.sampleCount || 1),
        aspectRatio: jobForm.aspectRatio.trim() || '3:4',
        notes: jobForm.notes.trim(),
        forceRegenerate: jobForm.forceRegenerate === true,
        storeFinalOnly: jobForm.storeFinalOnly !== false,
        generationFlow: 'gemini_flash_image_edit',
      };
      const resp = await authedFetch('/api/v1/lookbook/generation-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        if (String(data?.code || '').trim().toUpperCase() === 'LOOKBOOK_PROMPT_REQUIRED') {
          const missingConfig = data?.missingPromptConfig || {};
          await openPromptSettings({
            specId: String(missingConfig?.fabricSpecId || '').trim(),
            pendingAction: async () => {
              const rerunResp = await authedFetch('/api/v1/lookbook/generation-jobs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              });
              const rerunData = await rerunResp.json();
              if (!rerunResp.ok || rerunData?.success === false) {
                throw new Error(rerunData?.message || 'Failed to queue generation job.');
              }
              setJobForm(emptyJobForm);
              const usedFlow = generationFlowOverride || jobForm.generationFlow || 'gemini_flash_image_edit';
              setMessage(rerunData?.message || `Generation job queued (${usedFlow}).`);
              await loadData();
            },
          });
        }
        throw new Error(data?.message || 'Failed to queue generation job.');
      }
      setJobForm(emptyJobForm);
      const usedFlow = generationFlowOverride || jobForm.generationFlow || 'gemini_flash_image_edit';
      setMessage(data?.message || `Generation job queued (${usedFlow}).`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to queue generation job.');
    } finally {
      setBusyJobCreate(false);
    }
  }, [authedFetch, jobForm, loadData, openPromptSettings]);

  const handleQueueAndRunGeminiFlashImageEditJob = useCallback(async (event) => {
    event.preventDefault();
    setBusyJobCreate(true);
    setBusyRunJob(true);
    setError('');
    setMessage('');
    try {
      if (!String(jobForm.shopifyProductId || '').trim()) {
        throw new Error('Select a product first.');
      }
      const payload = {
        shopifyProductId: jobForm.shopifyProductId.trim(),
        productImageUrl: jobForm.productImageUrl.trim(),
        fabricImageUrl: jobForm.fabricImageUrl.trim(),
        sampleCount: Number(jobForm.sampleCount || 1),
        aspectRatio: jobForm.aspectRatio.trim() || '3:4',
        notes: jobForm.notes.trim(),
        forceRegenerate: jobForm.forceRegenerate === true,
        storeFinalOnly: true,
        generationFlow: 'gemini_flash_image_edit',
      };
      const resp = await authedFetch('/api/v1/lookbook/generation-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        if (String(data?.code || '').trim().toUpperCase() === 'LOOKBOOK_PROMPT_REQUIRED') {
          const missingConfig = data?.missingPromptConfig || {};
          await openPromptSettings({
            specId: String(missingConfig?.fabricSpecId || '').trim(),
            pendingAction: async () => {
              const rerunResp = await authedFetch('/api/v1/lookbook/generation-jobs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              });
              const rerunData = await rerunResp.json();
              if (!rerunResp.ok || rerunData?.success === false) {
                throw new Error(rerunData?.message || 'Failed to queue Gemini 2.5 Flash Image Edit job.');
              }
              const queuedJobIdRetry = String(rerunData?.job?.id || '').trim();
              if (!queuedJobIdRetry) {
                throw new Error('Gemini 2.5 Flash Image Edit job was queued but no job id was returned.');
              }
              const runRetryResp = await authedFetch('/api/v1/lookbook/generation-jobs/run-next', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ jobId: queuedJobIdRetry }),
              });
              const runRetryData = await runRetryResp.json();
              if (!runRetryResp.ok || runRetryData?.success === false) {
                throw new Error(runRetryData?.message || 'Failed to run queued Gemini 2.5 Flash Image Edit job.');
              }
              setMessage(runRetryData?.message || rerunData?.message || 'Gemini 2.5 Flash Image Edit completed.');
              await loadData();
            },
          });
        }
        throw new Error(data?.message || 'Failed to queue Gemini 2.5 Flash Image Edit job.');
      }
      const queuedJobId = String(data?.job?.id || '').trim();
      if (!queuedJobId) {
        throw new Error('Gemini 2.5 Flash Image Edit job was queued but no job id was returned.');
      }
      const runResp = await authedFetch('/api/v1/lookbook/generation-jobs/run-next', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId: queuedJobId }),
      });
      const runData = await runResp.json();
      if (!runResp.ok || runData?.success === false) {
        throw new Error(runData?.message || 'Failed to run queued Gemini 2.5 Flash Image Edit job.');
      }
      setMessage(runData?.message || data?.message || 'Gemini 2.5 Flash Image Edit completed.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to queue and run Gemini 2.5 Flash Image Edit.');
    } finally {
      setBusyJobCreate(false);
      setBusyRunJob(false);
    }
  }, [authedFetch, jobForm, loadData, openPromptSettings]);

  const handleQueueCollection = useCallback(async (event) => {
    event.preventDefault();
    setBusyCollectionQueue(true);
    setError('');
    setMessage('');
    try {
      if (!String(collectionForm.collectionGid || '').trim()) {
        throw new Error('Select a collection first.');
      }
      const payload = {
        collectionGid: collectionForm.collectionGid,
        sampleCount: 1,
        aspectRatio: collectionForm.aspectRatio.trim() || 'random',
        forceRegenerate: collectionForm.forceRegenerate === true,
        storeFinalOnly: collectionForm.storeFinalOnly !== false,
        generationFlow: 'gemini_flash_image_edit',
      };
      let cursor = null;
      let hasMore = true;
      let pageGuard = 0;
      let addedCount = 0;
      let totalCount = 0;
      const skippedByReason = new Map();
      const missingPromptByFabric = new Map();
      while (hasMore) {
        pageGuard += 1;
        if (pageGuard > 200) {
          throw new Error('Collection queue aborted: too many pages processed in one run.');
        }
        const resp = await authedFetch('/api/v1/lookbook/generation-jobs/queue-collection', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...payload,
            cursor,
            pageSize: 120,
          }),
        });
        const data = await resp.json();
        if (!resp.ok || data?.success === false) {
          throw new Error(data?.message || 'Failed to queue collection jobs.');
        }

        const results = Array.isArray(data?.results) ? data.results : [];
        const pageAdded = Number.isFinite(Number(data?.queuedCount))
          ? Math.max(0, Number(data.queuedCount))
          : results.filter((item) => item?.queued === true).length;
        const pageTotal = Number.isFinite(Number(data?.totalConsidered))
          ? Math.max(0, Number(data.totalConsidered))
          : results.length;
        addedCount += pageAdded;
        totalCount += pageTotal;

        results.forEach((item) => {
          if (item?.queued === true) return;
          const reasonKey = String(item?.reason || item?.message || 'other').trim() || 'other';
          skippedByReason.set(reasonKey, (skippedByReason.get(reasonKey) || 0) + 1);
        });

        const pageMissing = Array.isArray(data?.missingPromptFabrics) ? data.missingPromptFabrics : [];
        pageMissing.forEach((item) => {
          const fabric = String(item?.fabric || '').trim();
          const count = Number.isFinite(Number(item?.count)) ? Math.max(0, Number(item.count)) : 0;
          if (!fabric || count <= 0) return;
          missingPromptByFabric.set(fabric, (missingPromptByFabric.get(fabric) || 0) + count);
        });

        hasMore = data?.hasMore === true;
        cursor = hasMore ? String(data?.nextCursor || '').trim() || null : null;
        setMessage(`Queueing collection... processed ${totalCount} products so far.`);
      }

      const skippedCount = Math.max(0, totalCount - addedCount);
      const reasonLabel = (reasonKey) => {
        const key = String(reasonKey || '').trim().toLowerCase();
        if (key === 'job_exists') return 'Already queued';
        if (key === 'already_generated') return 'Already generated / not eligible';
        if (key === 'missing_prompt') return 'Missing prompt';
        if (key === 'missing_fabric_spec') return 'Missing fabric specification';
        if (key === 'missing_sku') return 'Missing SKU mapping';
        if (key === 'missing_product_id') return 'Missing Shopify product id';
        if (key === 'missing_fabric_image') return 'Missing fabric image';
        if (key === 'error') return 'Other error';
        return reasonKey;
      };
      const reasonLines = Array.from(skippedByReason.entries())
        .map(([reason, count]) => `- ${reasonLabel(reason)}: ${count}`)
        .join('\n');
      const missingPromptFabrics = Array.from(missingPromptByFabric.entries())
        .map(([fabric, count]) => ({ fabric, count }))
        .filter((item) => item.fabric && item.count > 0)
        .sort((a, b) => (b.count - a.count) || a.fabric.localeCompare(b.fabric));
      const missingFabricLines = missingPromptFabrics
        .map((item) => `- ${item.fabric}: ${item.count}`)
        .join('\n');
      if (typeof window !== 'undefined') {
        window.alert(
          [
            'Collection Queue Report',
            `Added to queue: ${addedCount}`,
            `Not added: ${skippedCount}`,
            reasonLines ? `\nNot added reasons:\n${reasonLines}` : '',
            missingFabricLines ? `\nFabrics missing prompt:\n${missingFabricLines}` : '',
          ].filter(Boolean).join('\n')
        );
      }
      setMessage(`Queued ${addedCount} generation job(s) from ${totalCount} products.`);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to queue collection jobs.');
    } finally {
      setBusyCollectionQueue(false);
    }
  }, [authedFetch, collectionForm, loadData]);

  const handleJobCancel = useCallback(async (jobId) => {
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch(`/api/v1/lookbook/generation-jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to cancel generation job.');
      }
      setMessage(data?.message || 'Generation job cancelled.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to cancel generation job.');
    }
  }, [authedFetch, loadData]);

  const handleJobRetry = useCallback(async (jobId) => {
    setBusyRunJob(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch(`/api/v1/lookbook/generation-jobs/${jobId}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runNow: true }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to retry generation job.');
      }
      setMessage(data?.message || 'Generation job retried.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to retry generation job.');
    } finally {
      setBusyRunJob(false);
    }
  }, [authedFetch, loadData]);

  const handleJobRemove = useCallback(async (jobId) => {
    const normalizedJobId = String(jobId || '').trim();
    if (!normalizedJobId) return;
    const confirmed = typeof window !== 'undefined'
      ? window.confirm('Remove this generation job from queue/history list?')
      : true;
    if (!confirmed) return;

    setError('');
    setMessage('');
    try {
      const resp = await authedFetch(`/api/v1/lookbook/generation-jobs/${normalizedJobId}`, {
        method: 'DELETE',
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to remove generation job.');
      }
      setMessage(data?.message || 'Generation job removed.');
      await loadJobs(jobsPage);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to remove generation job.');
    }
  }, [authedFetch, jobsPage, loadData, loadJobs]);

  const handleRunNextJob = useCallback(async (jobId = '') => {
    setBusyRunJob(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/lookbook/generation-jobs/run-next', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId: String(jobId || '').trim() || null }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to run generation job.');
      }
      setMessage(data?.message || (data?.skipped ? 'No queued jobs found.' : 'Generation job completed.'));
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to run generation job.');
    } finally {
      setBusyRunJob(false);
    }
  }, [authedFetch, loadData]);

  const handleEntryAction = useCallback(async (entryId, action, payload = {}) => {
    setError('');
    setMessage('');
    const normalizedEntryId = String(entryId || '').trim();
    if (normalizedEntryId) {
      setProcessingEntryId(normalizedEntryId);
      setProcessingEntryAction(String(action || '').trim());
    }
    if (normalizedEntryId && String(action || '').trim() === 'approve') {
      const nowIso = new Date().toISOString();
      setEntries((prev) => prev.map((entry) => (
        entry.id === normalizedEntryId
          ? {
            ...entry,
            status: 'publish_queued',
            approvedAt: entry.approvedAt || nowIso,
            approvedBy: entry.approvedBy || (currentUser?.uid || 'system'),
            publishQueuedAt: entry.publishQueuedAt || nowIso,
          }
          : entry
      )));
    }
    try {
      const resp = await authedFetch(`/api/v1/lookbook/entries/${entryId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || `Failed to ${action} lookbook entry.`);
      }
      setMessage(data?.message || 'Lookbook entry updated.');
      refreshEntriesSilent();
    } catch (err) {
      setError(err.message || `Failed to ${action} lookbook entry.`);
      await loadData();
    } finally {
      setProcessingEntryId((prev) => (prev === normalizedEntryId ? '' : prev));
      setProcessingEntryAction('');
    }
  }, [authedFetch, loadData, refreshEntriesSilent, currentUser]);

  const handleEntryDelete = useCallback(async (entry) => {
    const entryId = String(entry?.id || '').trim();
    if (!entryId) return;
    const productId = String(entry?.shopifyProductId || '').trim();
    const confirmed = typeof window !== 'undefined'
      ? window.confirm(`Delete this lookbook entry for product ${productId || 'unknown'}?`)
      : true;
    if (!confirmed) return;

    setError('');
    setMessage('');
    setProcessingEntryId(entryId);
    setProcessingEntryAction('delete');
    try {
      const resp = await authedFetch(`/api/v1/lookbook/entries/${entryId}`, {
        method: 'DELETE',
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to delete lookbook entry.');
      }
      setMessage(data?.message || 'Lookbook entry deleted.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete lookbook entry.');
    } finally {
      setProcessingEntryId((prev) => (prev === entryId ? '' : prev));
      setProcessingEntryAction('');
    }
  }, [authedFetch, loadData]);

  const selectedPromptSpec = useMemo(
    () => promptSpecs.find((spec) => spec.id === selectedPromptSpecId) || null,
    [promptSpecs, selectedPromptSpecId]
  );
  const selectedCollection = useMemo(
    () => collections.find((collection) => String(collection.id || '') === String(collectionForm.collectionGid || '')) || null,
    [collectionForm.collectionGid, collections]
  );

  const handlePromptSpecSearchChange = useCallback(async (value) => {
    const next = String(value || '');
    setPromptSettingsSearch(next);
    await loadPromptSpecs(next);
  }, [loadPromptSpecs]);

  const mutateSelectedSpecPrompts = useCallback((updater) => {
    setPromptSpecs((prev) => prev.map((spec) => {
      if (spec.id !== selectedPromptSpecId) return spec;
      const currentPrompts = Array.isArray(spec.prompts) ? spec.prompts : [];
      const updatedPrompts = updater(currentPrompts);
      return {
        ...spec,
        prompts: updatedPrompts,
        activePromptCount: updatedPrompts.filter((prompt) => prompt.active !== false).length,
      };
    }));
  }, [selectedPromptSpecId]);

  const handleAddPromptToSelectedSpec = useCallback(() => {
    const text = String(newPromptText || '').replace(/\s+/g, ' ').trim();
    if (!selectedPromptSpecId) return;
    if (!text) {
      setError('Prompt text is required.');
      return;
    }
    mutateSelectedSpecPrompts((prompts) => {
      const duplicate = prompts.some((item) => String(item.text || '').trim().toLowerCase() === text.toLowerCase());
      if (duplicate) return prompts;
      return [
        ...prompts,
        {
          id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          text,
          active: true,
          stats: { generated: 0, approved: 0, rejected: 0, published: 0 },
          createdAt: null,
          createdBy: null,
        },
      ];
    });
    setNewPromptText('');
  }, [mutateSelectedSpecPrompts, newPromptText, selectedPromptSpecId]);

  const handlePromptTextChange = useCallback((promptId, value) => {
    mutateSelectedSpecPrompts((prompts) => prompts.map((prompt) => (
      String(prompt.id) === String(promptId)
        ? { ...prompt, text: String(value || '') }
        : prompt
    )));
  }, [mutateSelectedSpecPrompts]);

  const handlePromptActiveToggle = useCallback((promptId) => {
    mutateSelectedSpecPrompts((prompts) => prompts.map((prompt) => (
      String(prompt.id) === String(promptId)
        ? { ...prompt, active: !(prompt.active !== false) }
        : prompt
    )));
  }, [mutateSelectedSpecPrompts]);

  const handlePromptDeleteFromSelectedSpec = useCallback((promptId) => {
    mutateSelectedSpecPrompts((prompts) => prompts.filter((prompt) => String(prompt.id) !== String(promptId)));
  }, [mutateSelectedSpecPrompts]);

  const mutateSelectedSpecMixConfig = useCallback((updater) => {
    setPromptSpecs((prev) => prev.map((spec) => {
      if (spec.id !== selectedPromptSpecId) return spec;
      const currentMix = spec?.mixConfig && typeof spec.mixConfig === 'object'
        ? spec.mixConfig
        : { mensShirtEnabled: false, mensShirtPct: 0 };
      const nextMix = updater(currentMix) || currentMix;
      return {
        ...spec,
        mixConfig: {
          mensShirtEnabled: nextMix.mensShirtEnabled === true,
          mensShirtPct: Math.max(0, Math.min(100, Number(nextMix.mensShirtPct) || 0)),
        },
      };
    }));
  }, [selectedPromptSpecId]);

  const handleMensShirtEnabledToggle = useCallback((enabled) => {
    mutateSelectedSpecMixConfig((current) => ({
      ...current,
      mensShirtEnabled: enabled === true,
      mensShirtPct: enabled === true ? Math.max(0.1, Number(current.mensShirtPct) || 8) : 0,
    }));
  }, [mutateSelectedSpecMixConfig]);

  const handleMensShirtPctChange = useCallback((value) => {
    mutateSelectedSpecMixConfig((current) => ({
      ...current,
      mensShirtPct: Math.max(0, Math.min(100, Number(value) || 0)),
    }));
  }, [mutateSelectedSpecMixConfig]);

  const handleSaveSelectedPromptSpec = useCallback(async () => {
    if (!selectedPromptSpec) return;
    setPromptSettingsSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        prompts: (Array.isArray(selectedPromptSpec.prompts) ? selectedPromptSpec.prompts : []).map((prompt) => ({
          id: String(prompt.id || '').startsWith('tmp-') ? '' : String(prompt.id || '').trim(),
          text: String(prompt.text || '').replace(/\s+/g, ' ').trim(),
          active: prompt.active !== false,
        })),
        mensShirtEnabled: selectedPromptSpec?.mixConfig?.mensShirtEnabled === true,
        mensShirtPct: Math.max(0, Math.min(100, Number(selectedPromptSpec?.mixConfig?.mensShirtPct) || 0)),
      };
      const resp = await authedFetch(`/api/v1/lookbook/fabric-prompts/${selectedPromptSpec.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to save lookbook prompts.');
      }
      setMessage(data?.message || 'Lookbook prompts updated.');
      const specs = await loadPromptSpecs(promptSettingsSearch);
      const activeCount = specs.find((spec) => spec.id === selectedPromptSpec.id)?.activePromptCount || 0;
      if (activeCount > 0 && typeof pendingQueueActionRef.current === 'function') {
        const pendingAction = pendingQueueActionRef.current;
        pendingQueueActionRef.current = null;
        setPromptSettingsOpen(false);
        await pendingAction();
      }
    } catch (err) {
      setError(err.message || 'Failed to save lookbook prompts.');
    } finally {
      setPromptSettingsSaving(false);
    }
  }, [authedFetch, loadPromptSpecs, promptSettingsSearch, selectedPromptSpec]);

  const summary = useMemo(() => ({
    totalEntries: entries.length,
    approvedEntries: entries.filter((entry) => ['approved', 'publish_queued', 'published'].includes(entry.status)).length,
    activeEntries: entries.filter((entry) => entry.active === true).length,
    currentDisplays: entries.filter((entry) => entry.isCurrentDisplay === true).length,
  }), [entries]);

  const resolvePublishAfterIso = useCallback(() => {
    if (!publishAfterInput) return null;
    const dt = new Date(publishAfterInput);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
  }, [publishAfterInput]);

  return (
    <div className="lookbook-admin">
      <div className="lookbook-admin__header">
        <div>
          <div className="lookbook-admin__title-row">
            <h1>Lookbook</h1>
            <button
              type="button"
              className="lookbook-admin__settings-icon"
              onClick={() => openPromptSettings()}
              aria-label="Lookbook prompt settings"
              title="Lookbook prompt settings"
            >
              ⚙
            </button>
          </div>
          <p>Manage generated lookbook entries, approval, scheduling, and current display selection.</p>
        </div>
        <div className="lookbook-admin__header-actions">
          <button
            type="button"
            className="lookbook-admin__refresh"
            onClick={handleShowExtraPromptRules}
            disabled={extraPromptRulesLoading}
          >
            {extraPromptRulesLoading ? 'Loading rules...' : (extraPromptRulesOpen ? 'Hide added prompt rules' : 'Show added prompt rules')}
          </button>
          <button type="button" className="lookbook-admin__refresh" onClick={() => handleRunNextJob()} disabled={busyRunJob}>
            {busyRunJob ? 'Running job...' : 'Run next job'}
          </button>
          <button type="button" className="lookbook-admin__refresh" onClick={loadData} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {extraPromptRulesOpen && (
        <div className="lookbook-admin__panel lookbook-admin__panel--compact">
          <h2>Added Prompt Rules</h2>
          <div className="lookbook-admin__prompt-rules-note">
            These rules are appended after the fabric-spec prompt for every generation request.
          </div>
          <div className="lookbook-admin__prompt-rules-editor">
            <label>
              Edit rules (one per line)
              <textarea
                value={extraPromptRulesDraft}
                onChange={(e) => setExtraPromptRulesDraft(e.target.value)}
                rows={Math.max(3, Math.min(10, extraPromptRulesDraft.split('\n').length + 1))}
              />
            </label>
            <div className="lookbook-admin__prompt-rules-actions">
              <button type="button" onClick={handleSaveExtraPromptRules} disabled={extraPromptRulesSaving}>
                {extraPromptRulesSaving ? 'Saving...' : 'Save rules'}
              </button>
            </div>
          </div>
          <ul className="lookbook-admin__prompt-rules-list">
            {extraPromptRules.map((rule, idx) => (
              <li key={`extra-rule-${idx + 1}`}>{rule}</li>
            ))}
          </ul>
        </div>
      )}

      {(error || message) && (
        <div className={`lookbook-admin__banner ${error ? 'is-error' : 'is-success'}`}>
          {error || message}
        </div>
      )}

      <section className="lookbook-admin__summary">
        <div className="lookbook-admin__summary-card">
          <span>Total entries</span>
          <strong>{summary.totalEntries}</strong>
        </div>
        <div className="lookbook-admin__summary-card">
          <span>Approved/published</span>
          <strong>{summary.approvedEntries}</strong>
        </div>
        <div className="lookbook-admin__summary-card">
          <span>Active</span>
          <strong>{summary.activeEntries}</strong>
        </div>
        <div className="lookbook-admin__summary-card">
          <span>Current display</span>
          <strong>{summary.currentDisplays}</strong>
        </div>
      </section>

      <section className="lookbook-admin__panel">
        <div className="lookbook-admin__panel-header">
          <h2>Daily Generation Cost</h2>
          <div className="lookbook-admin__metrics-controls">
            <label>
              Days
              <select value={dailyMetricsDays} onChange={(e) => setDailyMetricsDays(Number(e.target.value) || 30)}>
                <option value={7}>7</option>
                <option value={14}>14</option>
                <option value={30}>30</option>
                <option value={60}>60</option>
                <option value={90}>90</option>
              </select>
            </label>
            <label>
              Cost / generation
              <input
                type="number"
                min="0"
                step="0.0001"
                value={dailyUnitCost}
                onChange={(e) => setDailyUnitCost(e.target.value)}
                placeholder="0.0000"
              />
            </label>
            <button type="button" className="lookbook-admin__refresh" onClick={loadDailyMetrics} disabled={dailyMetricsLoading}>
              {dailyMetricsLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="lookbook-admin__metrics-summary">
          <div><strong>Total generated:</strong> {Number(dailyMetrics?.totals?.generated || 0)}</div>
          <div><strong>Estimated cost:</strong> {Number(dailyMetrics?.totals?.estimatedCost || 0).toFixed(4)}</div>
          <div><strong>Timezone:</strong> {dailyMetrics?.timeZone || '—'}</div>
        </div>

        <div className="lookbook-admin__metrics-table-wrap">
          <table className="lookbook-admin__metrics-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Generated</th>
                <th>Approved</th>
                <th>Rejected</th>
                <th>Published</th>
                <th>Est. Cost</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(dailyMetrics.rows) ? dailyMetrics.rows : []).map((row) => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{Number(row.generated || 0)}</td>
                  <td>{Number(row.approved || 0)}</td>
                  <td>{Number(row.rejected || 0)}</td>
                  <td>{Number(row.published || 0)}</td>
                  <td>{Number(row.estimatedCost || 0).toFixed(4)}</td>
                </tr>
              ))}
              {!dailyMetricsLoading && (!dailyMetrics.rows || dailyMetrics.rows.length === 0) && (
                <tr>
                  <td colSpan={6}>No daily metrics found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="lookbook-admin__panel">
        <h2>Queue Generation Job</h2>
        <div className="lookbook-admin__picker">
          <label className="is-wide">
            Search product
            <input
              value={jobProductSearch}
              onChange={(e) => searchProducts(e.target.value)}
              placeholder="Search by product title, SKU, handle, or Shopify product ID"
            />
          </label>
          {productSearchLoading && <div className="lookbook-admin__picker-note">Searching products...</div>}
          {!!productResults.length && (
            <div className="lookbook-admin__picker-results">
              {productResults.map((product) => (
                <button
                  type="button"
                  key={product.id}
                  className={`lookbook-admin__picker-result ${selectedProduct?.id === product.id ? 'is-selected' : ''}`}
                  onClick={() => {
                    setSelectedProduct(product);
                    setJobForm((prev) => ({
                      ...prev,
                      shopifyProductId: product.id,
                      productImageUrl: product.imageUrl || '',
                      fabricImageUrl: product.imageUrl || '',
                    }));
                  }}
                >
                  <span>{product.title || product.id}</span>
                  <small>{product.sku || product.id}</small>
                </button>
              ))}
            </div>
          )}
          {selectedProduct && (
            <div className="lookbook-admin__selected-product">
              <strong>Selected:</strong> {selectedProduct.title} ({selectedProduct.id})
            </div>
          )}
        </div>
        <form className="lookbook-admin__form" onSubmit={handleCreateJob}>
          <input type="hidden" value={jobForm.shopifyProductId} readOnly />
          <input type="hidden" value={jobForm.productImageUrl} readOnly />
          <input type="hidden" value={jobForm.fabricImageUrl} readOnly />
          <input type="hidden" value={jobForm.sampleCount} readOnly />
          <label>
            Aspect ratio
            <select
              value={jobForm.aspectRatio}
              onChange={(e) => setJobForm((prev) => ({ ...prev, aspectRatio: e.target.value }))}
            >
              <option value="1:1">1:1</option>
              <option value="3:4">3:4</option>
              <option value="4:3">4:3</option>
              <option value="9:16">9:16</option>
              <option value="16:9">16:9</option>
            </select>
          </label>
          <label className="is-wide">
            Notes
            <textarea
              value={jobForm.notes}
              onChange={(e) => setJobForm((prev) => ({ ...prev, notes: e.target.value }))}
              rows={2}
              placeholder="Optional notes for reviewer or generation worker"
            />
          </label>
          <label className="lookbook-admin__checkbox">
            <input
              type="checkbox"
              checked={jobForm.forceRegenerate}
              onChange={(e) => setJobForm((prev) => ({ ...prev, forceRegenerate: e.target.checked }))}
            />
            Force regenerate
          </label>
          <div className="lookbook-admin__form-actions is-wide">
            <button type="submit" disabled={busyJobCreate}>
              {busyJobCreate ? 'Queueing...' : 'Queue generation job'}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busyJobCreate || busyRunJob}
              onClick={handleQueueAndRunGeminiFlashImageEditJob}
            >
              {busyJobCreate || busyRunJob ? 'Running...' : 'Gemini 2.5 Flash Image Edit'}
            </button>
          </div>
        </form>
      </section>

      <section className="lookbook-admin__panel">
        <h2>Queue Collection</h2>
        <form className="lookbook-admin__form" onSubmit={handleQueueCollection}>
          <div className="lookbook-admin__picker is-wide">
            <label className="is-wide">
              Search collection
              <input
                value={collectionSearch}
                onChange={(e) => {
                  const next = e.target.value;
                  setCollectionSearch(next);
                  loadCollections(next);
                }}
                placeholder="Search collection title or handle"
              />
            </label>
            {!!String(collectionSearch || '').trim() && !!collections.length && (
              <div className="lookbook-admin__picker-results">
                {collections.slice(0, 50).map((collection) => (
                  <button
                    type="button"
                    key={collection.id}
                    className={`lookbook-admin__picker-result ${selectedCollection?.id === collection.id ? 'is-selected' : ''}`}
                    onClick={() => setCollectionForm((prev) => ({ ...prev, collectionGid: collection.id }))}
                  >
                    <span>{collection.title || collection.handle || collection.id}</span>
                    <small>{collection.products_count ?? '—'} products</small>
                  </button>
                ))}
              </div>
            )}
            {selectedCollection && (
              <div className="lookbook-admin__selected-product">
                <strong>Selected:</strong> {selectedCollection.title || selectedCollection.handle || selectedCollection.id}
              </div>
            )}
          </div>
          <label>
            Aspect ratio
            <select
              value={collectionForm.aspectRatio}
              onChange={(e) => setCollectionForm((prev) => ({ ...prev, aspectRatio: e.target.value }))}
            >
              <option value="random">Random</option>
              <option value="1:1">1:1</option>
              <option value="3:4">3:4</option>
              <option value="4:3">4:3</option>
              <option value="9:16">9:16</option>
              <option value="16:9">16:9</option>
            </select>
          </label>
          <label className="lookbook-admin__checkbox">
            <input
              type="checkbox"
              checked={collectionForm.forceRegenerate}
              onChange={(e) => setCollectionForm((prev) => ({ ...prev, forceRegenerate: e.target.checked }))}
            />
            Force regenerate
          </label>
          <div className="lookbook-admin__form-actions is-wide">
            <button type="submit" disabled={busyCollectionQueue}>
              {busyCollectionQueue ? 'Queueing collection...' : 'Queue collection jobs'}
            </button>
          </div>
        </form>
      </section>

      <section className="lookbook-admin__panel">
        <button
          type="button"
          className="lookbook-admin__accordion-toggle"
          onClick={() => setJobsOpen((prev) => !prev)}
        >
          <span>Generation Jobs</span>
          <span>{jobsOpen ? 'Hide' : 'Show'}</span>
        </button>

        {jobsOpen && (
          <>
            <div className="lookbook-admin__states">
              {jobs.map((job) => {
                const intermediateModelImages = extractIntermediateModelImages(job);
                return (
                  <article key={job.id} className="lookbook-admin__state-card">
                    <div>
                      <strong>{job.productSnapshot?.title || job.shopifyProductId}</strong>
                      <p>Status: {job.status}</p>
                      <p>Product: {job.shopifyProductId}</p>
                      <p>Flow: {job.generationFlow || 'gemini_flash_image_edit'}</p>
                      <p>Samples: {job.sampleCount || 1} | Ratio: {job.aspectRatio || '3:4'}</p>
                      <p>Fabric image: {job.fabricImageUrl || '—'}</p>
                      <p>Prompt: {job.prompt || '—'}</p>
                      <p>Requested: {formatDateTime(job.createdAt)}</p>
                      {intermediateModelImages.length > 0 && (
                        <div className="lookbook-admin__compare-block">
                          <p>Temporary model candidates:</p>
                          <div className="lookbook-admin__compare-grid">
                            {intermediateModelImages.map((item) => (
                              <a
                                key={item.key}
                                className="lookbook-admin__compare-item"
                                href={item.resolvedUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img src={item.resolvedUrl} alt={`Model candidate ${Number(item.candidateIndex || 0) + 1}`} />
                                <span>Model {Number(item.candidateIndex || 0) + 1}</span>
                                {item.temporaryExpiresAt && (
                                  <small>Expires: {formatDateTime(item.temporaryExpiresAt)}</small>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {job.lastError && (
                        <p className="lookbook-admin__job-error">Error: {job.lastError}</p>
                      )}
                    </div>
                    {['queued', 'running'].includes(String(job.status || '')) && (
                      <div className="lookbook-admin__job-actions">
                        {String(job.status || '') === 'queued' && (
                          <button type="button" className="secondary" onClick={() => handleRunNextJob(job.id)} disabled={busyRunJob}>
                            Run now
                          </button>
                        )}
                        <button type="button" className="secondary" onClick={() => handleJobCancel(job.id)}>
                          Cancel
                        </button>
                        <button type="button" className="secondary" onClick={() => handleJobRemove(job.id)}>
                          Remove
                        </button>
                      </div>
                    )}
                    {String(job.status || '') === 'failed' && (
                      <div className="lookbook-admin__job-actions">
                        <button type="button" className="secondary" onClick={() => handleJobRetry(job.id)} disabled={busyRunJob}>
                          {busyRunJob ? 'Retrying...' : 'Retry'}
                        </button>
                        <button type="button" className="secondary" onClick={() => handleJobRemove(job.id)}>
                          Remove
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
              {!jobs.length && !jobsLoading && (
                <div className="lookbook-admin__empty">No generation jobs found yet.</div>
              )}
              {jobsLoading && (
                <div className="lookbook-admin__empty">Loading generation jobs...</div>
              )}
            </div>
            <div className="lookbook-admin__pagination">
              <button
                type="button"
                className="secondary"
                disabled={jobsLoading || jobsPage <= 1}
                onClick={() => setJobsPage((prev) => Math.max(1, prev - 1))}
              >
                Prev
              </button>
              <span>Page {jobsPage} / {jobsTotalPages} ({jobsTotalCount} total)</span>
              <button
                type="button"
                className="secondary"
                disabled={jobsLoading || jobsPage >= jobsTotalPages}
                onClick={() => setJobsPage((prev) => Math.min(jobsTotalPages, prev + 1))}
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>

      <section className="lookbook-admin__panel">
        <div className="lookbook-admin__panel-header">
          <h2>Entries</h2>
          <div className="lookbook-admin__panel-header-controls">
            <div className="lookbook-admin__panel-search">
              <input
                type="text"
                placeholder="Search product id"
                value={entriesProductSearch}
                onChange={(e) => {
                  setEntriesProductSearch(e.target.value);
                  setEntriesPage(1);
                }}
              />
              {entriesProductSearch ? (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setEntriesProductSearch('');
                    setEntriesPage(1);
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
            <select
              value={createdSortDir}
              onChange={(e) => {
                setCreatedSortDir(e.target.value === 'asc' ? 'asc' : 'desc');
                setEntriesPage(1);
              }}
            >
              <option value="desc">Created: Newest first</option>
              <option value="asc">Created: Oldest first</option>
            </select>
            <select
              value={entriesCollectionGid}
              onChange={(e) => {
                setEntriesCollectionGid(e.target.value);
                setEntriesPage(1);
              }}
            >
              <option value="">All collections</option>
              {collections.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.title || collection.handle || collection.id}
                </option>
              ))}
            </select>
            <div className="lookbook-admin__panel-schedule">
              <input
                type="datetime-local"
                title="Publish after (local time)"
                placeholder="Publish after"
                value={publishAfterInput}
                onChange={(e) => setPublishAfterInput(e.target.value)}
              />
              {publishAfterInput ? (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setPublishAfterInput('')}
                >
                  Clear
                </button>
              ) : null}
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setEntriesPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="pending_generation">Pending generation</option>
              <option value="generated">Generated</option>
              <option value="publish_queued">Publish queued</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="published">Published</option>
              <option value="publish_failed">Publish failed</option>
            </select>
          </div>
        </div>

        <div className="lookbook-admin__entries">
          {entries.map((entry) => {
            const intermediateModelImages = extractIntermediateModelImages(entry);
            const rawSteps = Array.isArray(entry?.publishSync?.steps) ? entry.publishSync.steps : [];
            const stepMap = new Map(rawSteps.map((step) => [String(step?.name || '').trim(), step]));
            const entryImageUrl = resolveMediaUrl(entry.masterImageUrl || entry.shopifyImageUrl || '');
            const productImageUrl = resolveMediaUrl(
              entry?.productSnapshot?.imageUrl
              || entry?.productImageUrl
              || ''
            );
            const entryPrompt = String(
              entry?.prompt
              || entry?.generationParams?.prompt
              || entry?.generationParams?.promptText
              || entry?.generationParams?.finalPrompt
              || ''
            ).trim();
            return (
              <article key={entry.id} className="lookbook-admin__entry-card">
                <div className="lookbook-admin__entry-top">
                  <div className="lookbook-admin__entry-images">
                    <div className="lookbook-admin__entry-image-block">
                      <div className="lookbook-admin__entry-image-label">Generated</div>
                      {entryImageUrl ? (
                        <a href={entryImageUrl} target="_blank" rel="noreferrer" className="lookbook-admin__entry-image-link">
                          <img
                            src={entryImageUrl}
                            alt={entry.title || entry.productSnapshot?.title || 'Lookbook entry'}
                          />
                        </a>
                      ) : (
                        <div className="lookbook-admin__entry-image-link is-empty">No image</div>
                      )}
                    </div>
                    <div className="lookbook-admin__entry-image-block">
                      <div className="lookbook-admin__entry-image-label">Product</div>
                      {productImageUrl ? (
                        <a href={productImageUrl} target="_blank" rel="noreferrer" className="lookbook-admin__entry-image-link">
                          <img
                            src={productImageUrl}
                            alt={entry.productSnapshot?.title || entry.title || 'Product image'}
                          />
                        </a>
                      ) : (
                        <div className="lookbook-admin__entry-image-link is-empty">No product image</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3>{entry.title || entry.productSnapshot?.title || entry.shopifyProductId}</h3>
                    <p>Status: <strong>{entry.status}</strong></p>
                    <p>Product: {entry.shopifyProductId}</p>
                    <p>Active: {entry.active ? 'Yes' : 'No'} | Current: {entry.isCurrentDisplay ? 'Yes' : 'No'}</p>
                    <p>Display: {formatDateTime(entry.displayStartAt)} | {formatDateTime(entry.displayEndAt)}</p>
                    <p className="lookbook-admin__entry-prompt">Prompt: {entryPrompt || '—'}</p>
                    <p className="lookbook-admin__publish-steps">
                      Publish steps:{' '}
                      {PUBLISH_STEP_ORDER.map((name, idx) => {
                        const step = stepMap.get(name);
                        const status = String(step?.status || 'pending').trim().toLowerCase();
                        const cls =
                          status === 'ok' ? 'is-ok'
                            : status === 'failed' ? 'is-failed'
                              : status === 'skipped' ? 'is-skipped'
                                : 'is-pending';
                        return (
                          <React.Fragment key={`${entry.id}-${name}`}>
                            <span className={`lookbook-admin__step-chip ${cls}`}>
                              {name}
                            </span>
                            {idx < PUBLISH_STEP_ORDER.length - 1 ? ' -> ' : ''}
                          </React.Fragment>
                        );
                      })}
                    </p>
                    {intermediateModelImages.length > 0 && (
                      <div className="lookbook-admin__compare-block">
                        <p>Temporary model candidates:</p>
                        <div className="lookbook-admin__compare-grid">
                          {intermediateModelImages.map((item) => (
                            <a
                              key={item.key}
                              className="lookbook-admin__compare-item"
                              href={item.resolvedUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img src={item.resolvedUrl} alt={`Model candidate ${Number(item.candidateIndex || 0) + 1}`} />
                              <span>Model {Number(item.candidateIndex || 0) + 1}</span>
                              {item.temporaryExpiresAt && (
                                <small>Expires: {formatDateTime(item.temporaryExpiresAt)}</small>
                              )}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {entry.caption && <p className="lookbook-admin__entry-caption">{entry.caption}</p>}
                <div className="lookbook-admin__entry-actions">
                  <button
                    type="button"
                    onClick={() => {
                      const publishAfterIso = resolvePublishAfterIso();
                      handleEntryAction(entry.id, 'approve', publishAfterIso ? { publishAfter: publishAfterIso } : {});
                    }}
                    disabled={processingEntryId === entry.id}
                  >
                    {processingEntryId === entry.id && processingEntryAction === 'approve' ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => handleEntryAction(entry.id, 'reject', { reason: 'Rejected in review' })}
                    disabled={processingEntryId === entry.id}
                  >
                    {processingEntryId === entry.id && processingEntryAction === 'reject' ? 'Regenerating...' : 'Reject'}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => handleEntryDelete(entry)}
                    disabled={processingEntryId === entry.id}
                  >
                    {processingEntryId === entry.id && processingEntryAction === 'delete' ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </article>
            );
          })}
          {!entries.length && !loading && (
            <div className="lookbook-admin__empty">No entries found.</div>
          )}
        </div>
        <div className="lookbook-admin__pagination">
          <button
            type="button"
            className="secondary"
            disabled={loading || entriesPage <= 1}
            onClick={() => setEntriesPage((prev) => Math.max(1, prev - 1))}
          >
            Prev
          </button>
          <span>Page {entriesPage} / {entriesTotalPages} ({entriesTotalCount} total)</span>
          <button
            type="button"
            className="secondary"
            disabled={loading || entriesPage >= entriesTotalPages}
            onClick={() => setEntriesPage((prev) => Math.min(entriesTotalPages, prev + 1))}
          >
            Next
          </button>
        </div>
      </section>

      <section className="lookbook-admin__panel">
        <button
          type="button"
          className="lookbook-admin__accordion-toggle"
          onClick={() => setStatesOpen((prev) => !prev)}
        >
          <span>Product Queue State</span>
          <span>{statesOpen ? 'Hide' : 'Show'}</span>
        </button>

        {statesOpen && (
          <div className="lookbook-admin__states">
            {states.map((state) => (
              <article key={state.productId} className="lookbook-admin__state-card">
                <div>
                  <strong>{state.productTitle || state.productId}</strong>
                  <p>Stage: {state.generationStage}</p>
                  <p>Approved: {state.approvedEntryCount || 0} | Published: {state.publishedEntryCount || 0}</p>
                  <p>Current display entry: {state.currentDisplayEntryId || '—'}</p>
                </div>
              </article>
            ))}
            {!states.length && !statesLoading && (
              <div className="lookbook-admin__empty">No product states found yet.</div>
            )}
            {statesLoading && (
              <div className="lookbook-admin__empty">Loading product queue state...</div>
            )}
          </div>
        )}
      </section>

      {promptSettingsOpen && (
        <div className="lookbook-admin__modal-backdrop" role="presentation">
          <div className="lookbook-admin__modal" role="dialog" aria-modal="true" aria-label="Lookbook prompt settings">
            <div className="lookbook-admin__modal-header">
              <h2>Lookbook Prompt Settings</h2>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setPromptSettingsOpen(false);
                  pendingQueueActionRef.current = null;
                }}
              >
                Close
              </button>
            </div>
            <p className="lookbook-admin__modal-note">
              Prompts are managed by fabric and applied to all matching fabric specifications.
            </p>
            <div className="lookbook-admin__modal-body">
              <div className="lookbook-admin__spec-list">
                <label>
                  Search fabric
                  <input
                    value={promptSettingsSearch}
                    onChange={(e) => handlePromptSpecSearchChange(e.target.value)}
                    placeholder="Search by fabric name"
                  />
                </label>
                <div className="lookbook-admin__spec-items">
                  {promptSpecs.map((spec) => (
                    <button
                      key={spec.id}
                      type="button"
                      className={`lookbook-admin__spec-item ${selectedPromptSpecId === spec.id ? 'is-selected' : ''}`}
                      onClick={() => setSelectedPromptSpecId(spec.id)}
                    >
                      <strong>{spec.label}</strong>
                      <small>Active prompts: {spec.activePromptCount || 0}</small>
                      <small>Applies to specs: {spec.appliesToCount || 1}</small>
                    </button>
                  ))}
                  {!promptSpecs.length && !promptSettingsLoading && (
                    <div className="lookbook-admin__empty">No fabric specifications found.</div>
                  )}
                  {promptSettingsLoading && (
                    <div className="lookbook-admin__empty">Loading fabric specifications...</div>
                  )}
                </div>
              </div>

              <div className="lookbook-admin__prompt-editor">
                {!selectedPromptSpec && <div className="lookbook-admin__empty">Select a fabric specification to manage prompts.</div>}
                {selectedPromptSpec && (
                  <>
                    <div className="lookbook-admin__prompt-editor-header">
                      <strong>{selectedPromptSpec.label}</strong>
                    </div>
                    <div className="lookbook-admin__mix-config">
                      <label className="lookbook-admin__mix-config-toggle">
                        <input
                          type="checkbox"
                          checked={selectedPromptSpec?.mixConfig?.mensShirtEnabled === true}
                          onChange={(e) => handleMensShirtEnabledToggle(e.target.checked)}
                        />
                        <span>Enable men shirt mix for this fabric</span>
                      </label>
                      <label className="lookbook-admin__mix-config-pct">
                        Men shirt percentage
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={Number(selectedPromptSpec?.mixConfig?.mensShirtPct || 0)}
                          onChange={(e) => handleMensShirtPctChange(e.target.value)}
                          disabled={selectedPromptSpec?.mixConfig?.mensShirtEnabled !== true}
                        />
                      </label>
                      <small className="lookbook-admin__mix-config-note">
                        Example: 8 means ~8% jobs generate men-shirt variant for this fabric; remaining jobs use normal prompts.
                      </small>
                    </div>
                    <div className="lookbook-admin__prompt-add">
                      <input
                        value={newPromptText}
                        onChange={(e) => setNewPromptText(e.target.value)}
                        placeholder="Add prompt text"
                      />
                      <button type="button" className="secondary" onClick={handleAddPromptToSelectedSpec}>
                        Add prompt
                      </button>
                    </div>
                    <div className="lookbook-admin__prompt-list">
                      {(Array.isArray(selectedPromptSpec.prompts) ? selectedPromptSpec.prompts : []).map((prompt) => {
                        const stats = prompt?.stats || {};
                        const approved = Number(stats.approved || 0);
                        const rejected = Number(stats.rejected || 0);
                        const generated = Number(stats.generated || 0);
                        const published = Number(stats.published || 0);
                        const judged = approved + rejected;
                        const approvalRate = judged > 0 ? ((approved / judged) * 100).toFixed(1) : '—';
                        const rejectionRate = judged > 0 ? ((rejected / judged) * 100).toFixed(1) : '—';
                        return (
                          <div className="lookbook-admin__prompt-item" key={prompt.id}>
                            <textarea
                              rows={2}
                              value={prompt.text || ''}
                              onChange={(e) => handlePromptTextChange(prompt.id, e.target.value)}
                            />
                            <div className="lookbook-admin__prompt-meta">
                              <span>Generated: {generated}</span>
                              <span>Approved: {approved}</span>
                              <span>Rejected: {rejected}</span>
                              <span>Published: {published}</span>
                              <span>Approval: {approvalRate === '—' ? '—' : `${approvalRate}%`}</span>
                              <span>Rejection: {rejectionRate === '—' ? '—' : `${rejectionRate}%`}</span>
                            </div>
                            <div className="lookbook-admin__prompt-actions">
                              <button
                                type="button"
                                className="secondary"
                                onClick={() => handlePromptActiveToggle(prompt.id)}
                              >
                                {prompt.active !== false ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                type="button"
                                className="secondary"
                                onClick={() => handlePromptDeleteFromSelectedSpec(prompt.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {!Array.isArray(selectedPromptSpec.prompts) || !selectedPromptSpec.prompts.length ? (
                        <div className="lookbook-admin__empty">No prompts configured for this fabric spec.</div>
                      ) : null}
                    </div>
                    <div className="lookbook-admin__modal-actions">
                      <button
                        type="button"
                        onClick={handleSaveSelectedPromptSpec}
                        disabled={promptSettingsSaving}
                      >
                        {promptSettingsSaving ? 'Saving...' : 'Save prompts'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LookbookPage;
